// src/features/admin/components/settings/integrations/components/APIKeysTab.tsx
import React, { useState } from 'react';
import {
  KeyIcon,
  PlusIcon,
  EyeIcon,
  EyeSlashIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

interface APIKey {
  id: string;
  name: string;
  displayName: string;
  description: string;
  status: 'active' | 'expired' | 'invalid';
  lastUsed: string | null;
  maskedValue: string;
  expiresAt: string | null;
  category: string;
}

const API_KEYS: APIKey[] = [
  {
    id: '1',
    name: 'sendgrid',
    displayName: 'SendGrid',
    description: 'Email delivery service for outbound mail',
    status: 'active',
    lastUsed: '2 minutes ago',
    maskedValue: 'SG._cte****************************lxgjR0',
    expiresAt: null,
    category: 'Email'
  },
  {
    id: '2',
    name: 'openai',
    displayName: 'OpenAI',
    description: 'AI/ML services for Lex assistant',
    status: 'active',
    lastUsed: '5 minutes ago',
    maskedValue: 'sk-****************************',
    expiresAt: null,
    category: 'AI'
  },
  {
    id: '3',
    name: 'google_maps',
    displayName: 'Google Maps',
    description: 'Address validation and geocoding',
    status: 'active',
    lastUsed: '1 hour ago',
    maskedValue: 'AIza****************************',
    expiresAt: null,
    category: 'Utilities'
  }
];

const AVAILABLE_KEYS = [
  { name: 'twilio', displayName: 'Twilio', description: 'SMS notifications and 2FA', category: 'Communication' },
  { name: 'stripe', displayName: 'Stripe', description: 'Payment processing', category: 'Payments' },
  { name: 'docusign', displayName: 'DocuSign', description: 'Electronic signatures', category: 'Documents' },
  { name: 'quickbooks', displayName: 'QuickBooks', description: 'Accounting sync', category: 'Accounting' },
  { name: 'slack', displayName: 'Slack', description: 'Team notifications', category: 'Communication' },
];

const APIKeysTab: React.FC = () => {
  const [keys, setKeys] = useState<APIKey[]>(API_KEYS);
  const [showValues, setShowValues] = useState<Record<string, boolean>>({});
  const [showAddModal, setShowAddModal] = useState(false);

  const toggleShowValue = (keyId: string) => {
    setShowValues(prev => ({ ...prev, [keyId]: !prev[keyId] }));
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300">
            <CheckCircleIcon className="h-3 w-3 mr-1" />
            Active
          </span>
        );
      case 'expired':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
            <ClockIcon className="h-3 w-3 mr-1" />
            Expired
          </span>
        );
      case 'invalid':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300">
            <ExclamationCircleIcon className="h-3 w-3 mr-1" />
            Invalid
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <KeyIcon className="h-6 w-6 text-lex-accent mr-2" />
            API Keys Management
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage API credentials for third-party services. All keys are encrypted at rest.
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-lex-accent-contrast bg-lex-accent hover:bg-lex-accent-hover rounded-md transition-colors"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add API Key
        </button>
      </div>

      {/* Configured Keys */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h4 className="text-md font-semibold text-gray-900 dark:text-white">
            Configured API Keys ({keys.length})
          </h4>
        </div>
        
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {keys.map((key) => (
            <div key={key.id} className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg mr-4">
                    <KeyIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                  </div>
                  <div>
                    <div className="flex items-center">
                      <h5 className="text-sm font-semibold text-gray-900 dark:text-white">
                        {key.displayName}
                      </h5>
                      <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                        {key.category}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {key.description}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  {key.lastUsed && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Last used: {key.lastUsed}
                    </span>
                  )}
                  {getStatusBadge(key.status)}
                </div>
              </div>
              
              {/* Key Value Row */}
              <div className="mt-3 flex items-center justify-between bg-gray-50 dark:bg-gray-900/50 rounded-md px-3 py-2">
                <code className="text-xs text-gray-600 dark:text-gray-400 font-mono">
                  {showValues[key.id] ? 'sk-proj-XXXXX...XXXXX' : key.maskedValue}
                </code>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => toggleShowValue(key.id)}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    title={showValues[key.id] ? 'Hide' : 'Show'}
                  >
                    {showValues[key.id] ? (
                      <EyeSlashIcon className="h-4 w-4" />
                    ) : (
                      <EyeIcon className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                    title="Delete"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Available Integrations */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h4 className="text-md font-semibold text-gray-900 dark:text-white">
            Available Integrations
          </h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Add API keys to enable these integrations
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
          {AVAILABLE_KEYS.map((available) => (
            <div
              key={available.name}
              className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 hover:border-lex-accent hover:bg-lex-state-hover dark:hover:bg-lex-state-hover transition-colors cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="text-sm font-medium text-gray-900 dark:text-white">
                    {available.displayName}
                  </h5>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {available.description}
                  </p>
                </div>
                <PlusIcon className="h-5 w-5 text-gray-400" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Security Note */}
      <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
        <h5 className="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-1">
          Security Information
        </h5>
        <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-1">
          <li>• All API keys are encrypted using AES-256 before storage</li>
          <li>• Keys are stored in Google Cloud Secret Manager</li>
          <li>• Access to keys is logged in the audit trail</li>
          <li>• Rotate keys periodically for best security practices</li>
        </ul>
      </div>
    </div>
  );
};

export default APIKeysTab;
